import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  ActivityIndicator,
  Clipboard,
  Alert,
} from 'react-native';

const BACKEND_URL = 'https://eueke282zksk1zki18susjdksisk18sj.onrender.com';

type WhatsappStatus =
  | 'idle'
  | 'initializing'
  | 'waiting_pairing_code'
  | 'authenticated'
  | 'ready'
  | 'auth_failure'
  | 'disconnected';

interface WhatsappStatusResponse {
  success: boolean;
  connected: boolean;
  status: WhatsappStatus;
  phone: string | null;
  pairingCode: string | null;
  sessionBase64: string | null;
  error: string | null;
}

async function callWhatsappApi(action: string, extra?: Record<string, unknown>) {
  const res = await fetch(`${BACKEND_URL}/whatsapp`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action, ...extra }),
  });
  const data = await res.json();
  if (!res.ok || data.success === false) {
    throw new Error(data.error || 'Erreur inconnue');
  }
  return data as WhatsappStatusResponse;
}

export default function WhatsappConnectScreen() {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [status, setStatus] = useState<WhatsappStatus>('idle');
  const [phone, setPhone] = useState<string | null>(null);
  const [pairingCode, setPairingCode] = useState<string | null>(null);
  const [sessionBase64, setSessionBase64] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const sessionAlreadyShown = useRef(false);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const applyStatus = useCallback((data: WhatsappStatusResponse) => {
    setStatus(data.status);
    setPhone(data.phone);
    setPairingCode(data.pairingCode);
    setErrorMessage(data.error);

    if (data.status === 'ready' && data.sessionBase64 && !sessionAlreadyShown.current) {
      setSessionBase64(data.sessionBase64);
      sessionAlreadyShown.current = true;
      stopPolling();
    }
  }, [stopPolling]);

  const startPolling = useCallback(() => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const data = await callWhatsappApi('status');
        applyStatus(data);
      } catch (err: any) {
        setErrorMessage(err.message);
        stopPolling();
      }
    }, 3000);
  }, [applyStatus, stopPolling]);

  useEffect(() => {
    // Vérifie l'état au chargement de l'écran
    (async () => {
      try {
        const data = await callWhatsappApi('status');
        applyStatus(data);
        if (data.status !== 'idle' && data.status !== 'ready') {
          startPolling();
        }
      } catch {
        // Backend probablement en veille (Render free tier) — pas bloquant
      }
    })();
    return () => stopPolling();
  }, []);

  const handleConnect = async () => {
    setErrorMessage(null);
    if (!phoneNumber.trim()) {
      setErrorMessage('Entrez un numéro de téléphone.');
      return;
    }
    setLoading(true);
    try {
      const data = await callWhatsappApi('connect', { phone_number: phoneNumber.trim() });
      applyStatus(data);
      startPolling();
    } catch (err: any) {
      setErrorMessage(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    setErrorMessage(null);
    stopPolling();
    try {
      const data = await callWhatsappApi('logout');
      sessionAlreadyShown.current = false;
      setSessionBase64(null);
      setPairingCode(null);
      applyStatus(data);
    } catch (err: any) {
      setErrorMessage(err.message);
    }
  };

  const handleCopySession = () => {
    if (!sessionBase64) return;
    Clipboard.setString(sessionBase64);
    Alert.alert('Copié', 'La session a été copiée dans le presse-papiers.');
  };

  const dotColor =
    status === 'ready' ? '#25d366' :
    status === 'auth_failure' || status === 'disconnected' ? '#e05252' :
    status === 'idle' ? '#8a9a90' :
    '#e0b052';

  const statusLabel =
    status === 'ready' ? (phone ? `Connecté — ${phone}` : 'Connecté') :
    status === 'auth_failure' ? 'Échec de liaison' :
    status === 'disconnected' ? 'Déconnecté' :
    status === 'idle' ? 'Non connecté' :
    'Connexion en cours…';

  return (
    <ScrollView contentContainerStyle={styles.container} keyboardShouldPersistTaps="handled">
      <View style={styles.card}>
        <View style={styles.header}>
          <View style={[styles.dot, { backgroundColor: dotColor }]} />
          <Text style={styles.title}>WhatsApp</Text>
        </View>
        <Text style={styles.statusLabel}>{statusLabel}</Text>

        {status === 'idle' && (
          <View>
            <Text style={styles.label}>Numéro (format international, sans +)</Text>
            <TextInput
              style={styles.input}
              value={phoneNumber}
              onChangeText={setPhoneNumber}
              placeholder="22990000000"
              placeholderTextColor="#5c6b62"
              keyboardType="phone-pad"
            />
            <TouchableOpacity
              style={[styles.button, styles.buttonPrimary, loading && styles.buttonDisabled]}
              onPress={handleConnect}
              disabled={loading}
            >
              {loading ? (
                <ActivityIndicator color="#06210f" />
              ) : (
                <Text style={styles.buttonPrimaryText}>Connecter WhatsApp</Text>
              )}
            </TouchableOpacity>
          </View>
        )}

        {pairingCode && status !== 'ready' && (
          <View style={styles.pairingBlock}>
            <Text style={styles.hint}>
              Ouvrez WhatsApp → Appareils liés → Lier avec un numéro de téléphone, puis entrez :
            </Text>
            <Text style={styles.pairingCode}>{pairingCode}</Text>
            <Text style={styles.hint}>En attente de validation…</Text>
          </View>
        )}

        {sessionBase64 && (
          <View style={styles.sessionBlock}>
            <Text style={styles.label}>Session générée — copiez dans session.data.ts</Text>
            <ScrollView style={styles.sessionOutput} nestedScrollEnabled>
              <Text style={styles.sessionText}>{sessionBase64}</Text>
            </ScrollView>
            <TouchableOpacity style={[styles.button, styles.buttonCopy]} onPress={handleCopySession}>
              <Text style={styles.buttonCopyText}>Copier la session</Text>
            </TouchableOpacity>
          </View>
        )}

        {errorMessage && <Text style={styles.errorText}>{errorMessage}</Text>}

        {status !== 'idle' && (
          <View style={styles.footerActions}>
            <TouchableOpacity style={[styles.button, styles.buttonDanger]} onPress={handleLogout}>
              <Text style={styles.buttonDangerText}>Déconnecter</Text>
            </TouchableOpacity>
          </View>
        )}
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flexGrow: 1,
    backgroundColor: '#0e1210',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  card: {
    width: '100%',
    maxWidth: 420,
    backgroundColor: '#161c19',
    borderWidth: 1,
    borderColor: '#263029',
    borderRadius: 12,
    padding: 24,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginBottom: 4,
  },
  dot: {
    width: 9,
    height: 9,
    borderRadius: 5,
  },
  title: {
    fontSize: 17,
    fontWeight: '600',
    color: '#e8ede9',
  },
  statusLabel: {
    fontSize: 13,
    color: '#8a9a90',
    marginLeft: 19,
    marginBottom: 22,
  },
  label: {
    fontSize: 13,
    color: '#8a9a90',
    marginBottom: 6,
  },
  input: {
    backgroundColor: '#0e1210',
    borderWidth: 1,
    borderColor: '#263029',
    color: '#e8ede9',
    padding: 12,
    borderRadius: 8,
    fontSize: 14,
    marginBottom: 14,
  },
  button: {
    padding: 13,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  buttonPrimary: {
    backgroundColor: '#25d366',
  },
  buttonPrimaryText: {
    color: '#06210f',
    fontSize: 14,
    fontWeight: '600',
  },
  buttonDisabled: {
    opacity: 0.6,
  },
  buttonDanger: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: '#e05252',
  },
  buttonDangerText: {
    color: '#e05252',
    fontSize: 14,
    fontWeight: '600',
  },
  buttonCopy: {
    backgroundColor: '#263029',
  },
  buttonCopyText: {
    color: '#e8ede9',
    fontSize: 13,
    fontWeight: '600',
  },
  pairingBlock: {
    alignItems: 'center',
    marginVertical: 18,
    padding: 18,
    backgroundColor: '#0e1210',
    borderWidth: 1,
    borderColor: '#263029',
    borderRadius: 8,
  },
  pairingCode: {
    fontSize: 26,
    letterSpacing: 3,
    color: '#25d366',
    marginVertical: 6,
    fontWeight: '600',
  },
  hint: {
    fontSize: 12,
    color: '#8a9a90',
    textAlign: 'center',
    lineHeight: 18,
  },
  sessionBlock: {
    marginTop: 18,
  },
  sessionOutput: {
    maxHeight: 90,
    backgroundColor: '#0e1210',
    borderWidth: 1,
    borderColor: '#263029',
    borderRadius: 8,
    padding: 10,
    marginBottom: 10,
  },
  sessionText: {
    fontSize: 11,
    color: '#8a9a90',
    fontFamily: 'monospace',
  },
  errorText: {
    color: '#e05252',
    fontSize: 12,
    marginTop: 10,
  },
  footerActions: {
    marginTop: 20,
    paddingTop: 18,
    borderTopWidth: 1,
    borderTopColor: '#263029',
  },
});
